'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { Input } from '@brew/ui/input'
import { ArrowUp } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ROUNDED_SEARCH_INPUT_CLASSNAME,
  RoundedSearchBar,
} from '@/components/audience/rounded-search-bar'
import { WordRotate } from '@/components/magicui/word-rotate'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import { useDebouncedTemplateSearchQuery } from '@/hooks/use-public-template-search'
import type { Messages } from '@/lib/i18n/messages'
import {
  useTemplatesBasePath,
  useTemplatesSurface,
} from '../lib/templates-surface-context'
import { buildTemplatesUrl } from '../lib/url-helpers'

/** The placeholder's rotating examples, in order. */
const EXAMPLES: ReadonlyArray<
  keyof Messages['templates']['search']['examples']
> = [
  'brandsLikeStripe',
  'darkLuxury',
  'coralAndCream',
  'minimalWelcome',
  'boldLaunches',
]

const PLACEHOLDER_TEXT = 'font-medium text-muted-foreground text-sm'

/** The placeholder's words before the rotating example. */
function placeholderLead(chunks: ReactNode) {
  return <p className={PLACEHOLDER_TEXT}>{chunks}</p>
}

export function TemplatesSemanticSearchBar({
  currentType,
  currentBrand,
  currentDesigner,
}: {
  currentType?: string
  currentBrand?: string
  currentDesigner?: string
}) {
  const t = useTranslations('templates')
  const basePath = useTemplatesBasePath()
  const isPublicSurface = useTemplatesSurface() === 'public'
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const { draft, query, onDraftChange, onSubmit, onClear } =
    useDebouncedTemplateSearchQuery()
  const examples = useMemo(
    () => EXAMPLES.map((example) => t(`search.examples.${example}`)),
    [t]
  )
  const placeholderExample = useCallback(
    () => (
      <WordRotate
        words={examples}
        duration={4000}
        className={PLACEHOLDER_TEXT}
      />
    ),
    [examples]
  )

  useEffect(() => {
    // A committed query leaves a designer collection for the searchable
    // gallery — a designer is not an attribute of the semantic index. (The
    // flows gallery has a search bar of its own, `FlowsSearchBar`.)
    if (!query.trim() || !currentDesigner) {
      return
    }
    router.replace(
      buildTemplatesUrl({
        type: currentType,
        brand: currentBrand,
        q: query,
        basePath,
      })
    )
  }, [basePath, currentBrand, currentDesigner, currentType, query, router])

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      onSubmit()
    },
    [onSubmit]
  )

  const shouldShowPlaceholder = !isFocused && !draft

  return (
    <RoundedSearchBar
      onSubmit={handleSubmit}
      className={cn('h-11', { [MARKETING_CANVAS_BG]: isPublicSurface })}
    >
      <Input
        ref={inputRef}
        type="search"
        value={draft}
        placeholder=""
        aria-label={t('search.label')}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(event) => onDraftChange(event.currentTarget.value)}
        className={cn(ROUNDED_SEARCH_INPUT_CLASSNAME, 'h-full text-sm', {
          'bg-transparent': isPublicSurface,
        })}
      />
      {shouldShowPlaceholder ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1 left-4 mt-2 flex select-none items-center gap-1"
        >
          {/* One message, so a language can put the example where its
              grammar wants it. */}
          {t.rich('search.placeholder', {
            lead: placeholderLead,
            example: placeholderExample,
          })}
        </div>
      ) : null}
      {draft ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('search.clearSearch')}
          className="shrink-0 rounded-lg text-muted-foreground"
          onClick={onClear}
        >
          {t('search.clear')}
        </Button>
      ) : null}
      <Button
        type="submit"
        size="icon"
        aria-label={t('search.submit')}
        className="shrink-0 rounded-lg bg-foreground text-background"
        disabled={!draft.trim()}
      >
        <ArrowUp className="h-4 w-4" />
      </Button>
    </RoundedSearchBar>
  )
}
